import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import reportService from '../../services/reportService';
import { useProject } from '../../context/ProjectContext';
import { exportToExcel } from '../../utils/exportToExcel';
import Spinner from '../Common/Spinner';
import Alert from '../Common/Alert';
import Pagination from '../Common/Pagination';
import './ReportsHome.css';

const VISIT_EXPORT_COLUMNS = [
  { key: 'reportID', label: 'Report ID' },
  { key: 'scheduledVisitID', label: 'Scheduled Visit ID' },
  { key: 'reportStatus', label: 'Status' },
  { key: 'reportVersion', label: 'Version' },
  { key: 'sdvPercentageCompleted', label: 'SDV %' },
  { key: 'createdDate', label: 'Created Date' },
];

const ReportList = () => {
  const { currentProject: contextProject } = useProject();
  const projectCode = contextProject && contextProject !== 'all'
    ? (typeof contextProject === 'string' ? contextProject : contextProject?.projectCode ?? contextProject?.ProjectCode)
    : null;
  const [reports, setReports] = useState([]);
  const [loading, setLoading] = useState(true);
  const [exportLoading, setExportLoading] = useState(false);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(25);
  const [totalItems, setTotalItems] = useState(0);

  useEffect(() => {
    setLoading(true);
    reportService
      .getPaged(page, pageSize, projectCode || undefined)
      .then(({ data, pagination }) => {
        setReports(data || []);
        const p = pagination || {};
        setTotalItems(p.totalCount ?? p.TotalCount ?? 0);
      })
      .finally(() => setLoading(false));
  }, [page, pageSize, projectCode]);

  const handleExportExcel = async () => {
    setExportLoading(true);
    try {
      const data = await reportService.getAll(projectCode || undefined);
      const list = Array.isArray(data) ? data : [];
      const fileName = `VisitExecutionReports_${new Date().toISOString().slice(0, 10)}.xlsx`;
      exportToExcel(list, VISIT_EXPORT_COLUMNS, fileName);
    } finally {
      setExportLoading(false);
    }
  };

  if (loading) return <Spinner />;

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '12px', marginBottom: '16px' }}>
        <Link to="/reports" className="reports-back-link">← Back to Reports</Link>
        {totalItems > 0 && (
          <button
            type="button"
            className="export-excel-btn"
            onClick={handleExportExcel}
            disabled={exportLoading}
            title={exportLoading ? 'Exporting…' : 'Export to Excel'}
          >
            <span
              className="export-excel-icon"
              aria-hidden="true"
              style={{
                maskImage: `url(${process.env.PUBLIC_URL || ''}/excel-icon.png)`,
                WebkitMaskImage: `url(${process.env.PUBLIC_URL || ''}/excel-icon.png)`,
              }}
            />
          </button>
        )}
      </div>

      {reports.length === 0 && totalItems === 0 && <Alert type="info" message="No reports found" />}

      {reports.length > 0 && (
        <>
          <div className="table-wrap">
            <table className="audit-table reports-table">
              <thead>
                <tr>
                  <th>Report ID</th>
                  <th>Scheduled Visit</th>
                  <th>Status</th>
                  <th>Version</th>
                  <th>SDV %</th>
                  <th>Created Date</th>
                </tr>
              </thead>
              <tbody>
                {reports.map((r) => (
                  <tr key={r.reportID}>
                    <td>{r.reportID}</td>
                    <td>{r.scheduledVisitID ?? '-'}</td>
                    <td>{r.reportStatus}</td>
                    <td>{r.reportVersion}</td>
                    <td>{r.sdvPercentageCompleted != null ? `${Number(r.sdvPercentageCompleted).toFixed(1)}%` : '-'}</td>
                    <td>{r.createdDate ? new Date(r.createdDate).toLocaleString() : '-'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <Pagination
            page={page}
            pageSize={pageSize}
            totalItems={totalItems}
            onPageChange={setPage}
            onPageSizeChange={setPageSize}
          />
        </>
      )}
    </div>
  );
};

export default ReportList;
